import {
  User,
  UserRoundPlus,
  Mail,
  BookOpen,
  Shield,
  HelpCircle,
  FileText,
  ShieldCheck,
  ClipboardCheck,
  LogOut,
  Home,
  MoreVertical,
  HeartHandshake,
  LayoutDashboard,
  BadgeInfo,
  BarChart3,
  MessageCircleHeart,
  Rocket
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { Key } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services/api';
import { removeSensitiveData, clearAllData } from '../../utils/secureStorage';
import { useState} from "react";
import { useAuth } from '../../hooks/useAuth';
import Toast from '../../components/Toast/Toast';
import { useMenuLabels, type MenuLabels } from '../../hooks/useMenuLabels';
import { useAppSelector } from '../../store';
interface MenuProps {
  isLoggedIn?: boolean;
  onClose: () => void;
}

interface MenuItem {
  icon: React.ReactNode;
  label: string;
  labelId: keyof MenuLabels;
  page: string;
  showWhenLoggedIn?: boolean;
  showWhenLoggedOut?: boolean;
  requiresManagerAccess?: boolean;
  subItems?: SubMenuItem[];
}

interface SubMenuItem {
  icon: React.ReactNode;
  label: string;
  labelId: keyof MenuLabels;
  page: string;
}

export default function Menu({ isLoggedIn = true, onClose }: MenuProps) {
  // Clear refined match data when menu is opened
  
  const clearRefinedMatches = async () => {
    await removeSensitiveData("refinedMatches");
    await removeSensitiveData("refinedTotalMatches");
    await removeSensitiveData("isRefinedSearch");
  };

  clearRefinedMatches();


  const navigate = useNavigate();
  const { logout } = useAuth();
  const labels = useMenuLabels();
  const hasManagerCenterAccess = useAppSelector((s) => s.managerCenterAccess.hasAccess);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" | "warning" } | null>(null);

  const menuItems: MenuItem[] = [
    {
      icon: <LayoutDashboard className="w-5 h-5" />,
      label: 'Dashboard',
      labelId: 'dashboard',
      page: '/dashboard',

      showWhenLoggedIn: true,
      showWhenLoggedOut: false
    },
    {
      icon: <User className="w-5 h-5" />,
      label: 'Account',
      labelId: 'account',
      page: '/my-profile',
      showWhenLoggedIn: true,
      showWhenLoggedOut: false
    },
    {
      icon: <UserRoundPlus className="w-5 h-5" />,
      label: 'List',
      labelId: 'list',
      page: '/groups',
      showWhenLoggedIn: true,
      showWhenLoggedOut: false
    },
    {
      icon: <BadgeInfo className="w-5 h-5" />,
      label: 'Private List',
      labelId: 'privateList',
      page: '/saved-lists',
      showWhenLoggedIn: true,
      showWhenLoggedOut: false
    },
    {
      icon: <ClipboardCheck className="w-5 h-5" />,
      label: 'Availability Approvals',
      labelId: 'availabilityApprovals',
      page: '/availability-approvals',
      showWhenLoggedIn: true,
      showWhenLoggedOut: false,
      requiresManagerAccess: true
    },
  
    {
      icon: <HeartHandshake className="w-5 h-5" />,
      label: 'Connecting Information',
      labelId: 'connectingInformation',
      page: '/DesiredExperienceRange',
      showWhenLoggedIn: true,
      showWhenLoggedOut: true
    },
    {
      icon: <Mail className="w-5 h-5" />,
      label: 'Messages',
      labelId: 'messages',
      page: '/OverViewMessages',
      showWhenLoggedIn: true,
      showWhenLoggedOut: true
    },
    {
      icon: <User className="w-5 h-5" />,
      label: 'Account Login',
      labelId: 'accountLogin',
      page: 'login',
      showWhenLoggedIn: false,
      showWhenLoggedOut: true
    },
    {
      icon: <Home className="w-5 h-5" />,
      label: 'Home',
      labelId: 'home',
      page: 'home',
      showWhenLoggedIn: false,
      showWhenLoggedOut: true
    },
    {
      icon: <BookOpen className="w-5 h-5" />,
      label: 'Resources',
      labelId: 'resources',
      page: 'resources',
      showWhenLoggedIn: true,
      showWhenLoggedOut: true,
      subItems: [
        {
          icon: <Shield className="w-5 h-5" />,
          label: 'Online Safety',
          labelId: 'onlineSafety',
          page: '/OnlineSafety'
        },
        {
          icon: <Shield className="w-5 h-5" />,
          label: 'Child Safety',
          labelId: 'childSafety',
          page: '/childsafetyPrivate'
        },
        {
          icon: <HelpCircle className="w-5 h-5" />,
          label: 'Help Center',
          labelId: 'helpCenter',
          page: '/HelpCenter'
        },
        {
          icon: <FileText className="w-5 h-5" />,
          label: 'Terms & Conditions',
          labelId: 'termsConditions',
          page: '/TermsConditions'
        },
        {
          icon: <ShieldCheck className="w-5 h-5" />,
          label: 'Privacy Policy',
          labelId: 'privacyPolicy',
          page: '/PrivacyPolicyPrivate'
        },

      ]
    },
    {
      icon: <ShieldCheck className="w-5 h-5" />,
      label: 'Create Post',
      labelId: 'createPost',
      page: '/CreatePost',
      showWhenLoggedIn: true,
      showWhenLoggedOut: false
    },
    {
  icon: <ShieldCheck className="w-5 h-5" />,
  label: 'View Post',
  labelId: 'viewPost',
  page: '/viewPost',
  showWhenLoggedIn: true,        // ✅ ADD THIS
  showWhenLoggedOut: false       // ✅ OPTIONAL (recommended)
},
    {
      icon: <Key className="w-5 h-5" />,
      label: 'Change Password',
      labelId: 'changePassword',
      page: '/reset-password',
      showWhenLoggedIn: true,
      showWhenLoggedOut: false
    },
      {
      icon: <BarChart3 className="w-5 h-5" />,
      label: 'Reports',
      labelId: 'reports',
      page: '/reports',
      showWhenLoggedIn: true,
      showWhenLoggedOut: false,
      requiresManagerAccess: true
    },
    {
      icon: <BadgeInfo className="w-5 h-5" />,
      label: 'About us',
      labelId: 'about',
      page: '/about',
      showWhenLoggedIn: true,
      showWhenLoggedOut: true
    },
    {
      icon: <MessageCircleHeart className="w-5 h-5" />,
      label: 'My Feedback',
      labelId: 'myFeedback',
      page: '/my-feedback',
      showWhenLoggedIn: true,
      showWhenLoggedOut: false
    },
    {
      icon: <Rocket className="w-5 h-5" />,
      label: 'Wishlist',
      labelId: 'wishlist',
      page: '/wishlist',
      showWhenLoggedIn: true,
      showWhenLoggedOut: false
    },
    {
      icon: <LogOut className="w-5 h-5" />,
      label: 'Logout',
      labelId: 'logout',
      page: 'logout',
      showWhenLoggedIn: true,
      showWhenLoggedOut: false
    }
  ];

  const visibleMenuItems = menuItems.filter(item =>
    (isLoggedIn ? item.showWhenLoggedIn : item.showWhenLoggedOut) &&
    (!item.requiresManagerAccess || hasManagerCenterAccess)
  );
  const handleMenuItemClick = async (page: string) => {
    if (page === 'logout') {
      const result = await logout();
      if (result.success) {
        setToast({ message: 'You have been logged out successfully', type: 'success' });
      } else {
        setToast({ message: 'Logout failed', type: 'error' });
      }
      onClose(); // Close the mobile drawer
      return;
    }

    if (window.location.pathname === page) {
      onClose();
      return;
    }
   
    navigate(page);
  };
  return (
    <div className="h-screen bg-[#F5F5F5] flex flex-col h-full">
      <div className="bg-[#FFFFFF] h-full flex flex-col">
        <div className="flex items-center justify-between px-4 pt-6 pb-4">
          <h1 className="text-[28px] font-[700] leading-[1.2] text-[#212121] m-0">
            Menu
          </h1>
          <button
            onClick={onClose}
            className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center bg-transparent border-0 cursor-pointer"
            aria-label="Close menu"
          >
            <MoreVertical className="w-6 h-6 text-brand-primary" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-8">
          <nav className="flex flex-col" role="navigation" aria-label="Main menu">
            {visibleMenuItems.map((item, index) => (
              <div key={index}>
                {item.subItems ? (
                  <Accordion type="single" collapsible className="border-0">
                    <AccordionItem value={`item-${index}`} className="border-0">
                      <AccordionTrigger className="flex items-center gap-3 py-4 bg-transparent border-0 text-left cursor-pointer min-h-[44px] transition-opacity hover:opacity-70 hover:no-underline [&>svg]:hidden">
                        <div className="flex items-center gap-3 flex-1">
                          <span className="text-[#212121] flex items-center justify-center w-6">
                            {item.icon}
                          </span>
                          <span className="text-[16px] font-[400] text-[#212121]">
                            {labels[item.labelId] || item.label}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-0">
                        <div className="flex flex-col pl-9">
                          {item.subItems.map((subItem, subIndex) => (
                            <button
                              key={subIndex}
                              onClick={() => handleMenuItemClick(subItem.page)}
                              className="flex items-center gap-3 py-3 bg-transparent border-0 text-left cursor-pointer min-h-[44px] transition-opacity hover:opacity-70"
                              aria-label={labels[subItem.labelId] || subItem.label}
                            >
                              <span className="text-[#757575] flex items-center justify-center w-5">
                                {subItem.icon}
                              </span>
                              <span className="text-[14px] font-[400] text-[#757575]">
                                {labels[subItem.labelId] || subItem.label}
                              </span>
                            </button>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                ) : (
                  <button
                    onClick={() => handleMenuItemClick(item.page)}
                    className="flex items-center gap-3 py-4 bg-transparent border-0 text-left cursor-pointer min-h-[44px] transition-opacity hover:opacity-70 w-full"
                    aria-label={labels[item.labelId] || item.label}
                  >
                    <span className="text-[#212121] flex items-center justify-center w-6">
                      {item.icon}
                    </span>
                    <span className="text-[16px] font-[400] text-[#212121]">
                      {labels[item.labelId] || item.label}
                    </span>
                  </button>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
